import { 
  Lock, 
  Check, 
  AlertTriangle, 
  TrendingUp, 
  Unlock,
  ArrowBigUp,
  ArrowBigDown,
  Loader2,
  Shield, 
  Wallet, 
  Droplets, 
  Flame, 
  RefreshCw, 
  ArrowLeftRight, 
  ArrowUpDown, 
  Ban, 
  CircleDollarSign, 
  Clock, 
  Split, 
  Code, 
  BadgeCheck, 
  DollarSign, 
  Users, 
  X, 
  BarChart2,
  Code2
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { ethers } from 'ethers';
import { useLockerSettings } from '../../providers/LockerSettingsProvider';
import { useFirebaseFees } from "@/providers/FirebaseFeeProvider";
import { useNetwork } from 'wagmi';
import { ParticleBackgroundRedGreen } from '@/components/effects/ParticleBackgroundRedGreen';
import { useTheme } from "@/providers/ThemeProvider";
import { getTxOptions } from "@/utils/gas";
import { UserTokenSelector } from '@/components/token/UserTokenSelector';

// Common interfaces that we'll check for
const COMMON_INTERFACES = {
  BASIC: [
    // Basic ERC20 functions
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address) view returns (uint256)",
    "function owner() view returns (address)",
    "function renounceOwnership() returns ()",
  ],
  EXTENDED: [
    // Security Features - Max Wallet (Anti-Whale)
    "function maxWalletAmount() view returns (uint256)",
    "function maxWalletLocked() view returns (bool)",
    "function lockMaxWallet()",
    
    // Security Features - Max Buy (Anti-Pump)
    "function maxBuyAmount() view returns (uint256)",
    "function maxBuyLocked() view returns (bool)",
    "function lockMaxBuy()",
    
    // Security Features - Max Sell (Anti-Dump)
    "function maxSellAmount() view returns (uint256)",
    "function maxSellLocked() view returns (bool)",
    "function lockMaxSell()",
    
    // Security Features - Trading Delay (Anti-Sniper)
    "function tradingDelay() view returns (uint256)",
    "function tradingDelayLocked() view returns (bool)",
    "function lockTradingDelay()",
    
    // Trading Enabled Lock
    "function tradingEnabled() view returns (bool)",
    "function tradingEnabledLocked() view returns (bool)",
    "function lockTradingEnabled()",
    
    // Tax Collection
    "function buyTax() view returns (uint256)",
    "function sellTax() view returns (uint256)",
    "function buyTaxLocked() view returns (bool)",
    "function sellTaxLocked() view returns (bool)",
    "function lockBuyTax()",
    "function lockSellTax()",
    
    // Tax Distribution
    "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
    "function _taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
    "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
    "function reflectionLocked() view returns (bool)",
    "function rewardsLocked() view returns (bool)",
    "function reflectLocked() view returns (bool)",
    "function autoLiquidityLocked() view returns (bool)",
    "function burnLocked() view returns (bool)",
    "function marketingLocked() view returns (bool)",
    "function developmentLocked() view returns (bool)",
    "function lockDistribution()",
    "function contractLocked() view returns (bool)",
    "function lockContract()"
  ]
};

// Feature definitions with detection logic
const FEATURE_DEFINITIONS = [
  {
    id: "buyTax",
    name: "Buy Tax",
    description: "Configure your token's buy tax rate",
    icon: <ArrowBigUp className="w-5 h-5 text-purple-500" />,
    color: "purple",
    detect: async (contract) => {
      try {
        console.log("Detecting buyTax...");
        const buyTax = await contract.buyTax();
        console.log("buyTax value:", buyTax.toString());
        const isLocked = await contract.buyTaxLocked();
        console.log("buyTax locked:", isLocked);
        return {
          exists: true,
          lockable: true,
          isLocked,
          value: `${buyTax}%`
        };
      } catch (e) {
        console.error("Error detecting buyTax:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "sellTax",
    name: "Sell Tax",
    description: "Configure your token's sell tax rate",
    icon: <ArrowBigDown className="w-5 h-5 text-purple-500" />,
    color: "purple",
    detect: async (contract) => {
      try {
        console.log("Detecting sellTax...");
        const sellTax = await contract.sellTax();
        console.log("sellTax value:", sellTax.toString());
        const isLocked = await contract.sellTaxLocked();
        console.log("sellTax locked:", isLocked);
        return {
          exists: true,
          lockable: true,
          isLocked,
          value: `${sellTax}%`
        };
      } catch (e) {
        console.error("Error detecting sellTax:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "reflectionDistribution",
    name: "Reflection Distribution",
    description: "Configure reflection distribution percentage",
    icon: <RefreshCw className="w-5 h-5 text-orange-500" />,
    color: "orange",
    detect: async (contract) => {
      try {
        console.log("Detecting reflection distribution...");
        const distribution = await getTaxDistribution(contract);
        if (!distribution) return { exists: false };
        
        console.log("Got reflection rate:", distribution.reflection);
        if (distribution.reflection === "0") return { exists: false };
        
        return {
          exists: true,
          lockable: true,
          isLocked: false, // These are locked via contract locking
          value: `${distribution.reflection}%`
        };
      } catch (e) {
        console.error("Error detecting reflection distribution:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "autoLiquidityDistribution",
    name: "Auto-Liquidity Distribution",
    description: "Configure auto-liquidity distribution percentage",
    icon: <ArrowLeftRight className="w-5 h-5 text-orange-500" />,
    color: "orange",
    detect: async (contract) => {
      try {
        console.log("Detecting auto-liquidity distribution...");
        const distribution = await getTaxDistribution(contract);
        if (!distribution) return { exists: false };
        
        console.log("Got auto-liquidity rate:", distribution.autoLiquidity);
        if (distribution.autoLiquidity === "0") return { exists: false };
        
        return {
          exists: true,
          lockable: true,
          isLocked: false,
          value: `${distribution.autoLiquidity}%`
        };
      } catch (e) {
        console.error("Error detecting auto-liquidity distribution:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "burnDistribution",
    name: "Burn Distribution",
    description: "Configure burn distribution percentage",
    icon: <Flame className="w-5 h-5 text-orange-500" />,
    color: "orange",
    detect: async (contract) => {
      try {
        console.log("Detecting burn distribution...");
        const distribution = await getTaxDistribution(contract);
        if (!distribution) return { exists: false };
        
        console.log("Got burn rate:", distribution.burn);
        if (distribution.burn === "0") return { exists: false };
        
        return {
          exists: true,
          lockable: true,
          isLocked: false,
          value: `${distribution.burn}%`
        };
      } catch (e) {
        console.error("Error detecting burn distribution:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "marketingWalletDistribution",
    name: "Marketing Wallet Distribution",
    description: "Configure marketing wallet distribution percentage",
    icon: <Wallet className="w-5 h-5 text-orange-500" />,
    color: "orange",
    detect: async (contract) => {
      try {
        console.log("Detecting marketing distribution...");
        const distribution = await getTaxDistribution(contract);
        if (!distribution) return { exists: false };
        
        console.log("Got marketing rate:", distribution.marketing);
        if (distribution.marketing === "0") return { exists: false };
        
        return {
          exists: true,
          lockable: true,
          isLocked: false,
          value: `${distribution.marketing}%`
        };
      } catch (e) {
        console.error("Error detecting marketing distribution:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "developmentWalletDistribution",
    name: "Development Wallet Distribution",
    description: "Configure development wallet distribution percentage",
    icon: <Code className="w-5 h-5 text-orange-500" />,
    color: "orange",
    detect: async (contract) => {
      try {
        console.log("Detecting development distribution...");
        const distribution = await getTaxDistribution(contract);
        if (!distribution) return { exists: false };
        
        console.log("Got development rate:", distribution.development);
        if (distribution.development === "0") return { exists: false };
        
        return {
          exists: true,
          lockable: true,
          isLocked: false,
          value: `${distribution.development}%`
        };
      } catch (e) {
        console.error("Error detecting development distribution:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "maxWallet",
    name: "Max Wallet",
    description: "Maximum tokens per wallet (Anti-Whale)",
    icon: <Wallet className="w-5 h-5 text-blue-500" />,
    color: "blue",
    detect: async (contract) => {
      try {
        console.log("Detecting maxWallet...");
        
        // Check if any maxWallet function exists
        if (!checkFunctionExists(contract, FUNCTION_VARIATIONS.maxWalletAmount)) {
          console.log("No maxWallet function found");
          return { exists: false };
        }

        // Get maxWallet value
        const maxWallet = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxWalletAmount, "0");
        if (maxWallet === "0") {
          console.log("maxWallet value is 0");
          return { exists: false };
        }
        console.log("maxWallet value:", maxWallet.toString());

        // Get locked status
        const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxWalletLocked, false);
        console.log("maxWallet locked:", isLocked);
        return {
          exists: true,
          lockable: checkFunctionExists(contract, FUNCTION_VARIATIONS.maxWalletLocked),
          isLocked,
          value: `${maxWallet}%`
        };
      } catch (e) {
        console.error("Error detecting maxWallet:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "maxBuyAmount",
    name: "Max Buy Amount",
    description: "Maximum buy transaction amount (Anti-Pump)",
    icon: <ArrowBigUp className="w-5 h-5 text-green-500" />,
    color: "green",
    detect: async (contract) => {
      try {
        console.log("Detecting maxBuyAmount...");
        
        // Check if any maxBuy function exists
        if (!checkFunctionExists(contract, FUNCTION_VARIATIONS.maxBuyAmount)) {
          console.log("No maxBuy function found");
          return { exists: false };
        }

        // Get maxBuy value
        const maxBuy = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxBuyAmount, "0");
        if (maxBuy === "0") {
          console.log("maxBuy value is 0");
          return { exists: false };
        }
        console.log("maxBuyAmount value:", maxBuy.toString());

        // Get locked status
        const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxBuyLocked, false);
        console.log("maxBuyAmount locked:", isLocked);
        return {
          exists: true,
          lockable: checkFunctionExists(contract, FUNCTION_VARIATIONS.maxBuyLocked),
          isLocked,
          value: `${maxBuy}%`
        };
      } catch (e) {
        console.error("Error detecting maxBuyAmount:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "maxSellAmount",
    name: "Max Sell Amount",
    description: "Maximum sell transaction amount (Anti-Dump)",
    icon: <ArrowBigDown className="w-5 h-5 text-red-500" />,
    color: "red",
    detect: async (contract) => {
      try {
        console.log("Detecting maxSellAmount...");
        
        // Check if any maxSell function exists
        if (!checkFunctionExists(contract, FUNCTION_VARIATIONS.maxSellAmount)) {
          console.log("No maxSell function found");
          return { exists: false };
        }

        // Get maxSell value
        const maxSell = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxSellAmount, "0");
        if (maxSell === "0") {
          console.log("maxSell value is 0");
          return { exists: false };
        }
        console.log("maxSellAmount value:", maxSell.toString());

        // Get locked status
        const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxSellLocked, false);
        console.log("maxSellAmount locked:", isLocked);
        return {
          exists: true,
          lockable: checkFunctionExists(contract, FUNCTION_VARIATIONS.maxSellLocked),
          isLocked,
          value: `${maxSell}%`
        };
      } catch (e) {
        console.error("Error detecting maxSellAmount:", e);
        return { exists: false };
      }
    }
  },
  {
    id: "tradingDelay",
    name: "Trading Delay",
    description: "Trading delay after enabling (Anti-Sniper)",
    icon: <Clock className="w-5 h-5 text-yellow-500" />,
    color: "yellow",
    detect: async (contract) => {
      try {
        console.log("Detecting tradingDelay...");
        const delay = await contract.tradingDelay();
        console.log("tradingDelay value:", delay.toString());
        const isLocked = await contract.tradingDelayLocked();
        console.log("tradingDelay locked:", isLocked);
        return {
          exists: true,
          lockable: true,
          isLocked,
          value: `${delay.toString()} seconds`
        };
      } catch (e) {
        console.error("Error detecting tradingDelay:", e);
        return { exists: false };
      }
    }
  }
];

// Common function name variations
const FUNCTION_VARIATIONS = {
  // Tax settings
  buyTax: ["buyTax"],
  sellTax: ["sellTax"],
  buyTaxLocked: ["buyTaxLocked"],
  sellTaxLocked: ["sellTaxLocked"],
  lockBuyTax: ["lockBuyTax"],
  lockSellTax: ["lockSellTax"],

  // Distribution settings
  taxDistribution: ["taxDistribution", "_taxDistribution"],

  // Distribution locks
  reflectionLocked: ["reflectionLocked", "rewardsLocked", "reflectLocked"],
  liquidityLocked: ["autoLiquidityLocked"],
  burnLocked: ["burnLocked"],
  marketingLocked: ["marketingLocked"],
  developmentLocked: ["developmentLocked"],
  lockReflectionDistribution: ["lockReflection", "lockRewards", "lockReflect"],
  lockAutoLiquidityDistribution: ["lockAutoLiquidity"],
  lockBurnDistribution: ["lockBurn"],
  lockMarketingWalletDistribution: ["lockMarketing", "lockMarketingFee"], // Updated to use tax distribution lock functions
  lockDevelopmentWalletDistribution: ["lockDevelopment", "lockDevelopmentFee"], // Updated to use tax distribution lock functions
  // Security settings
  maxWalletAmount: ["maxWalletAmount"],
  maxWalletLocked: ["maxWalletLocked"],
  maxBuyAmount: ["maxBuyAmount"],
  maxBuyLocked: ["maxBuyLocked"],
  maxSellAmount: ["maxSellAmount"],
  maxSellLocked: ["maxSellLocked"],
  tradingDelay: ["tradingDelay"],
  tradingDelayLocked: ["tradingDelayLocked"],
  lockMaxWallet: ["lockMaxWallet"],
  lockMaxBuy: ["lockMaxBuy"],
  lockMaxSell: ["lockMaxSell"],
  lockTradingDelay: ["lockTradingDelay"],
  contractLocked: ["contractLocked"],
  lockContract: ["lockContract"],
  tradingEnabled: ["tradingEnabled"],
  tradingEnabledLocked: ["tradingEnabledLocked"],
  lockTradingEnabled: ["lockTradingEnabled"]
};

// ABI for tax-related functions
const TAX_ABI = [
  // Read functions
  "function buyTax() view returns (uint256)",
  "function sellTax() view returns (uint256)",
  "function buyTaxLocked() view returns (bool)",
  "function sellTaxLocked() view returns (bool)",
  "function autoLiquidityLocked() view returns (bool)",
  "function burnLocked() view returns (bool)",
  "function developmentLocked() view returns (bool)",
  "function marketingLocked() view returns (bool)",
  "function reflectionLocked() view returns (bool)",
  "function rewardsLocked() view returns (bool)",
  "function reflectLocked() view returns (bool)",
  // Write functions
  "function lockBuyTax() external",
  "function lockSellTax() external",
  "function lockAutoLiquidity() external",
  "function lockBurn() external",
  "function lockDevelopment() external",
  "function lockMarketing() external",
  "function lockReflection() external",
  "function lockRewards() external",
  "function lockReflect() external",
  // Wallet functions
  "function marketingWallets(uint256) view returns (address)",
  "function developmentWallets(uint256) view returns (address)",
  "function marketingWalletsLocked() view returns (bool)",
  "function developmentWalletsLocked() view returns (bool)",
  "function lockMarketingWallets()",
  "function lockDevelopmentWallets()"
];

// Update the ABI to match your contract's actual functions
const ERC20_ABI = [
  // Basic token info
  "function owner() view returns (address)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  
  // Tax Functions
  "function buyTax() view returns (uint256)",
  "function sellTax() view returns (uint256)",
  "function buyTaxLocked() view returns (bool)",
  "function sellTaxLocked() view returns (bool)",
  
  // Distribution Functions
  "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function _taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function reflectionLocked() view returns (bool)",
  "function rewardsLocked() view returns (bool)",
  "function reflectLocked() view returns (bool)",
  "function autoLiquidityLocked() view returns (bool)",
  "function burnLocked() view returns (bool)",
  "function marketingLocked() view returns (bool)",
  "function developmentLocked() view returns (bool)",
  
  // Security Functions
  "function maxWalletAmount() view returns (uint256)",
  "function maxBuyAmount() view returns (uint256)",
  "function maxSellAmount() view returns (uint256)",
  "function tradingDelay() view returns (uint256)",
  "function maxWalletLocked() view returns (bool)",
  "function maxBuyLocked() view returns (bool)",
  "function maxSellLocked() view returns (bool)",
  "function tradingDelayLocked() view returns (bool)",
  "function tradingEnabled() view returns (bool)",
  "function tradingEnabledLocked() view returns (bool)",
  "function lockMaxWallet() returns (bool)",
  "function lockMaxBuy() returns (bool)",
  "function lockMaxSell() returns (bool)",
  "function lockTradingDelay() returns (bool)",
  "function lockTradingEnabled() returns (bool)",
  "function renounceOwnership() returns (bool)",
  "function contractLocked() view returns (bool)",
  "function lockContract() returns (bool)"
];

const formatTaxValue = (value: any) => {
  try {
    if (!value) return "0";
    
    // Convert from Wei to Ether and handle decimal places
    const formatted = ethers.utils.formatUnits(value, 18);
    // Convert to percentage (multiply by 100)
    const percentage = parseFloat(formatted) * 100;
    // Format to 2 decimal places
    return percentage.toFixed(2);
  } catch (error) {
    console.error('Error formatting tax value:', error);
    return "0";
  }
};

const SecurityLocks = ({ contract }) => {
  const [features, setFeatures] = useState({
    maxWallet: { value: null, isLocked: false },
    maxBuy: { value: null, isLocked: false },
    maxSell: { value: null, isLocked: false },
    tradingDelay: { value: null, isLocked: false }
  });

  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  const [isOwner, setIsOwner] = useState(false);
  const { chain } = useNetwork();

  const fetchSecurityFeatures = async () => {
    try {
      // Max Wallet
      const maxWallet = await contract.maxWalletAmount();
      const maxWalletLocked = await contract.maxWalletLocked();
      const totalSupply = await contract.totalSupply();
      
      // Max Buy
      const maxBuy = await contract.maxBuyAmount();
      const maxBuyLocked = await contract.maxBuyLocked();
      
      // Max Sell
      const maxSell = await contract.maxSellAmount();
      const maxSellLocked = await contract.maxSellLocked();
      
      // Trading Delay
      const tradingDelay = await contract.tradingDelay();
      const tradingDelayLocked = await contract.tradingDelayLocked();

      // Max uint256 value for comparison
      const maxUint256 = "115792089237316195423570985008687907853269984665640564039457584007913129639935";

      // Calculate values with "No Limit" check
      const maxWalletValue = maxWallet.toString() === maxUint256 
        ? "No Limit" 
        : maxWallet.mul(100).div(totalSupply).toString() + '%';

      const maxBuyValue = maxBuy.toString() === maxUint256 
        ? "No Limit" 
        : maxBuy.mul(100).div(totalSupply).toString() + '%';

      const maxSellValue = maxSell.toString() === maxUint256 
        ? "No Limit" 
        : maxSell.mul(100).div(totalSupply).toString() + '%';

      setFeatures({
        maxWallet: { 
          value: maxWalletValue, 
          isLocked: maxWalletLocked 
        },
        maxBuy: { 
          value: maxBuyValue, 
          isLocked: maxBuyLocked 
        },
        maxSell: { 
          value: maxSellValue, 
          isLocked: maxSellLocked 
        },
        tradingDelay: { 
          value: tradingDelay.toString() + ' seconds', 
          isLocked: tradingDelayLocked 
        }
      });
    } catch (error) {
      console.error('Error fetching security features:', error);
      toast({
        title: "Error",
        description: "Failed to fetch security features",
        variant: "destructive",
      });
    }
  };

  const handleLock = async (feature) => {
    if (!contract) return;

    try {
      // Get the provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Platform fee transaction using Firebase fees
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
      const feeCollectorAddress = fees.collectors[0].address;
      
      // Send platform fee
      const feeTx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(chain?.id, 50000)
      });
      await feeTx.wait();

      // Get the lock function name based on the feature
      const lockFunctionMap = {
        maxWallet: 'lockMaxWallet',
        maxBuy: 'lockMaxBuy',
        maxSell: 'lockMaxSell',
        tradingDelay: 'lockTradingDelay'
      };

      const lockFunction = lockFunctionMap[feature];
      if (!lockFunction) {
        throw new Error(`Invalid feature: ${feature}`);
      }

      // Call the lock function
      const tx = await contractWithSigner[lockFunction]({
        ...getTxOptions(chain?.id, 500000)
      });
      await tx.wait();

      // Update the UI
      await fetchSecurityFeatures();

      toast({
        title: "Success",
        description: `${feature} has been locked successfully`,
      });
    } catch (error) {
      console.error(`Error locking ${feature}:`, error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const init = async () => {
      if (contract) {
        try {
          const owner = await contract.owner();
          setIsOwner(owner.toLowerCase() === userAddress?.toLowerCase());
          await fetchSecurityFeatures();
        } catch (error) {
          console.error('Error initializing:', error);
        }
      }
    };

    init();
  }, [contract, userAddress]);

  return (
    <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
      <div className="space-y-2 text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Security Locks</h2>
        <p className="text-sm text-muted-foreground">Configure security and trading limits</p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <Wallet className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-medium">Max Wallet</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.maxWallet.value}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLock('maxWallet')}
            disabled={features.maxWallet.isLocked || !isOwner}
            className="border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
          >
            {features.maxWallet.isLocked ? (
              <>
                <Lock className="w-4 h-4 mr-2 text-green-500" />
                Locked
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                Lock
              </>
            )}
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <ArrowBigUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-medium">Max Buy Amount</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.maxBuy.value}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLock('maxBuy')}
            disabled={features.maxBuy.isLocked || !isOwner}
            className="border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
          >
            {features.maxBuy.isLocked ? (
              <>
                <Lock className="w-4 h-4 mr-2 text-green-500" />
                Locked
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                Lock
              </>
            )}
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <ArrowBigDown className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="font-medium">Max Sell Amount</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.maxSell.value}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLock('maxSell')}
            disabled={features.maxSell.isLocked || !isOwner}
            className="border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
          >
            {features.maxSell.isLocked ? (
              <>
                <Lock className="w-4 h-4 mr-2 text-green-500" />
                Locked
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                Lock
              </>
            )}
          </Button>
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h3 className="font-medium">Trading Delay</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.tradingDelay.value}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleLock('tradingDelay')}
            disabled={features.tradingDelay.isLocked || !isOwner}
            className="border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
          >
            {features.tradingDelay.isLocked ? (
              <>
                <Lock className="w-4 h-4 mr-2 text-green-500" />
                Locked
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                Lock
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TaxDistributionLocks = ({ contract }) => {
  const [distribution, setDistribution] = useState(null);
  const [lockingFeature, setLockingFeature] = useState(null);
  const [transactionStep, setTransactionStep] = useState(0);
  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  const [isOwner, setIsOwner] = useState(false);
  const provider = new ethers.providers.Web3Provider(walletClient.transport);
  const { chain } = useNetwork();

  const refreshDistribution = async () => {
    if (contract) {
      const taxDist = await getTaxDistribution(contract);
      setDistribution(taxDist);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (contract) {
        await refreshDistribution();

        // Check if user is owner
        const owner = await contract.owner();
        setIsOwner(owner.toLowerCase() === userAddress?.toLowerCase());
      }
    };

    init();
  }, [contract, userAddress]);

  const handleLock = async (feature) => {
    if (!contract || !isOwner || !fees) {
      toast({
        title: "Error",
        description: "Unable to lock feature. Please check your connection and permissions.",
        variant: "destructive"
      });
      return;
    }

    try {
      setLockingFeature(feature);
      setTransactionStep(1);

      // Get signer and connect contract
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // First transaction: Pay platform fee using settings
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
      const feeCollectorAddress = fees.collectors[0].address;
      
      try {
        const feeTx = await signer.sendTransaction({
          to: feeCollectorAddress,
          value: platformFeeAmount,
          ...getTxOptions(chain?.id, 50000)
        });
        await feeTx.wait();
        
        toast({
          title: "Platform Fee Paid",
          description: "Successfully paid the platform fee. Please confirm the next transaction to lock the feature.",
        });
      } catch (error) {
        console.error("Error paying platform fee:", error);
        toast({
          title: "Error",
          description: "Failed to pay platform fee. The feature cannot be locked.",
          variant: "destructive",
        });
        setLockingFeature(null);
        setTransactionStep(0);
        return;
      }

      // Second transaction: Lock the feature
      setTransactionStep(2);
      
      // Find the correct lock function name
      const lockFunctionVariations = {
        reflection: ['lockReflectionFee', 'lockReflection', 'lockReflect', 'lockReflectionDistribution'],
        autoLiquidity: ['lockAutoLiquidityFee', 'lockAutoLiquidity', 'lockLiquidity', 'lockAutoLiquidityDistribution'],
        burn: ['lockBurnFee', 'lockBurn', 'lockBurnDistribution'],
        marketing: ['lockMarketingFee', 'lockMarketing', 'lockMarketingDistribution'],
        development: ['lockDevelopmentFee', 'lockDevelopment', 'lockDevelopmentDistribution']
      };

      const variations = lockFunctionVariations[feature];
      if (!variations) {
        throw new Error(`Invalid feature: ${feature}`);
      }

      // Try to find an available function
      let lockFunction = null;
      for (const funcName of variations) {
        if (typeof contractWithSigner[funcName] === 'function') {
          lockFunction = funcName;
          break;
        }
      }

      if (!lockFunction) {
        throw new Error(`No available lock function found for ${feature}`);
      }

      toast({
        title: "Locking Feature",
        description: "Please confirm the transaction to lock the feature...",
      });

      const tx = await contractWithSigner[lockFunction]({
        ...getTxOptions(chain?.id, 300000)
      });
      await tx.wait();

      // Update the distribution and lock status
      await refreshDistribution();

      toast({
        title: "Success",
        description: `${feature} has been locked successfully`,
      });
    } catch (error) {
      console.error(`Error locking ${feature}:`, error);
      toast({
        title: "Error",
        description: error.message || "Failed to lock feature",
        variant: "destructive",
      });
    } finally {
      setLockingFeature(null);
      setTransactionStep(0);
    }
  };

  if (!distribution) {
    return (
      <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Tax Distribution Locks</h2>
          <p className="text-sm text-muted-foreground">Loading tax distribution features...</p>
        </div>
      </div>
    );
  }

  const features = [
    { id: 'reflection', name: 'Reflection', value: distribution.reflection.value + '%', locked: distribution.reflection.locked, icon: RefreshCw },
    { id: 'autoLiquidity', name: 'Auto Liquidity', value: distribution.autoLiquidity.value + '%', locked: distribution.autoLiquidity.locked, icon: Droplets },
    { id: 'burn', name: 'Burn', value: distribution.burn.value + '%', locked: distribution.burn.locked, icon: Flame },
    { id: 'marketing', name: 'Marketing', value: distribution.marketing.value + '%', locked: distribution.marketing.locked, icon: BarChart2 },
    { id: 'development', name: 'Development', value: distribution.development.value + '%', locked: distribution.development.locked, icon: Code }
  ];

  return (
    <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
      <div className="space-y-2 text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Tax Distribution Locks</h2>
        <p className="text-sm text-muted-foreground">Lock tax distribution features</p>
      </div>

      <div className="space-y-4">
        {features.map((feature) => (
          <div key={feature.id} className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/10">
                <feature.icon className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="font-medium">{feature.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Current Value: {feature.value}
                </p>
              </div>
            </div>

            {feature.locked ? (
              <div className="bg-card px-3 py-1.5 rounded-lg border border-[#0a74ff]/50">
                <div className="flex items-center text-green-500 text-sm gap-1">
                  <Lock className="w-4 h-4" />
                  Locked
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLock(feature.id)}
                disabled={!isOwner || lockingFeature !== null}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
              >
                {lockingFeature === feature.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {transactionStep === 1 ? "Paying Fee..." : "Locking..."}
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Lock
                  </>
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const TaxRateLocks = ({ contract }) => {
  const [features, setFeatures] = useState({
    buyTax: { value: null, isLocked: false },
    sellTax: { value: null, isLocked: false }
  });
  const [lockingFeature, setLockingFeature] = useState(null);
  const [transactionStep, setTransactionStep] = useState(0);
  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  const [isOwner, setIsOwner] = useState(false);
  const provider = new ethers.providers.Web3Provider(walletClient.transport);
  const { chain } = useNetwork();

  const fetchTaxRateFeatures = async () => {
    try {
      if (!contract) return;

      // Get tax rate values
      const buyTax = await contract.buyTax();
      const sellTax = await contract.sellTax();
      
      // Get lock statuses
      const buyTaxLocked = await contract.buyTaxLocked();
      const sellTaxLocked = await contract.sellTaxLocked();

      setFeatures({
        buyTax: {
          value: buyTax.toString() + '%',
          isLocked: buyTaxLocked
        },
        sellTax: {
          value: sellTax.toString() + '%',
          isLocked: sellTaxLocked
        }
      });

      // Check if user is owner
      const owner = await contract.owner();
      setIsOwner(owner.toLowerCase() === userAddress?.toLowerCase());
    } catch (error) {
      console.error('Error fetching tax rate features:', error);
    }
  };

  useEffect(() => {
    if (contract) {
      fetchTaxRateFeatures();
    }
  }, [contract, userAddress]);

  const handleLock = async (feature) => {
    if (!contract || !isOwner || !fees) {
      toast({
        title: "Error",
        description: "Unable to lock feature. Please check your connection and permissions.",
        variant: "destructive"
      });
      return;
    }

    try {
      setLockingFeature(feature);
      setTransactionStep(1);

      // Get signer and connect contract
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // First transaction: Pay platform fee using settings
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
      const feeCollectorAddress = fees.collectors[0].address;
      
      try {
        const feeTx = await signer.sendTransaction({
          to: feeCollectorAddress,
          value: platformFeeAmount,
          ...getTxOptions(chain?.id, 50000)
        });
        await feeTx.wait();
        
        toast({
          title: "Platform Fee Paid",
          description: "Successfully paid the platform fee. Please confirm the next transaction to lock the feature.",
        });
      } catch (error) {
        console.error("Error paying platform fee:", error);
        toast({
          title: "Error",
          description: "Failed to pay platform fee. The feature cannot be locked.",
          variant: "destructive",
        });
        setLockingFeature(null);
        setTransactionStep(0);
        return;
      }

      // Second transaction: Lock the feature
      setTransactionStep(2);
      
      // Find the correct lock function name
      const lockFunctionMap = {
        buyTax: 'lockBuyTax',
        sellTax: 'lockSellTax'
      };

      const lockFunction = lockFunctionMap[feature];
      if (!lockFunction) {
        throw new Error(`Invalid feature: ${feature}`);
      }

      toast({
        title: "Locking Feature",
        description: "Please confirm the transaction to lock the feature...",
      });

      const tx = await contractWithSigner[lockFunction]({
        ...getTxOptions(chain?.id, 300000)
      });
      await tx.wait();

      // Update the UI
      await fetchTaxRateFeatures();

      toast({
        title: "Success",
        description: `${feature === 'buyTax' ? 'Buy tax' : 'Sell tax'} has been locked successfully`,
      });
    } catch (error) {
      console.error(`Error locking ${feature}:`, error);
      toast({
        title: "Error",
        description: error.message || "Failed to lock feature",
        variant: "destructive",
      });
    } finally {
      setLockingFeature(null);
      setTransactionStep(0);
    }
  };

  return (
    <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
      <div className="space-y-2 text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Tax Rate Locks</h2>
        <p className="text-sm text-muted-foreground">Configure buy and sell tax rates</p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <ArrowBigUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-medium">Buy Tax</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.buyTax.value}
              </p>
            </div>
          </div>

          {features.buyTax.isLocked ? (
            <div className="bg-card px-3 py-1.5 rounded-lg border border-[#0a74ff]/50">
              <div className="flex items-center text-green-500 text-sm gap-1">
                <Lock className="w-4 h-4" />
                Locked
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleLock('buyTax')}
              disabled={!isOwner || lockingFeature !== null}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
            >
              {lockingFeature === 'buyTax' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {transactionStep === 1 ? "Paying Fee..." : "Locking..."}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Lock
                </>
              )}
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-500/10">
              <ArrowBigDown className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="font-medium">Sell Tax</h3>
              <p className="text-sm text-muted-foreground">
                Current Value: {features.sellTax.value}
              </p>
            </div>
          </div>

          {features.sellTax.isLocked ? (
            <div className="bg-card px-3 py-1.5 rounded-lg border border-[#0a74ff]/50">
              <div className="flex items-center text-green-500 text-sm gap-1">
                <Lock className="w-4 h-4" />
                Locked
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleLock('sellTax')}
              disabled={!isOwner || lockingFeature !== null}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
            >
              {lockingFeature === 'sellTax' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {transactionStep === 1 ? "Paying Fee..." : "Locking..."}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Lock
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const detectMaxWallet = async (contract: any) => {
  const maxWalletFunctions = ['maxWalletAmount', 'maxWallet', '_maxWalletAmount'];
  const funcName = findAvailableFunction(contract, maxWalletFunctions);
  if (!funcName) return null;
  try {
    return await contract[funcName]();
  } catch (error) {
    console.error('Error detecting max wallet:', error);
    return null;
  }
};

const detectMaxBuyAmount = async (contract: any) => {
  const maxBuyFunctions = ['maxBuyAmount', 'maxBuy', '_maxBuyAmount'];
  const funcName = findAvailableFunction(contract, maxBuyFunctions);
  if (!funcName) return null;
  try {
    return await contract[funcName]();
  } catch (error) {
    console.error('Error detecting max buy:', error);
    return null;
  }
};

const detectMaxSellAmount = async (contract: any) => {
  const maxSellFunctions = ['maxSellAmount', 'maxSell', '_maxSellAmount'];
  const funcName = findAvailableFunction(contract, maxSellFunctions);
  if (!funcName) return null;
  try {
    return await contract[funcName]();
  } catch (error) {
    console.error('Error detecting max sell:', error);
    return null;
  }
};

const detectTradingDelay = async (contract: any) => {
  const tradingDelayFunctions = ['tradingDelay', '_tradingDelay', 'getTradingDelay'];
  const funcName = findAvailableFunction(contract, tradingDelayFunctions);
  if (!funcName) return null;
  try {
    return await contract[funcName]();
  } catch (error) {
    console.error('Error detecting trading delay:', error);
    return null;
  }
};

const detectMaxWalletLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);
    
    // Try to get the max wallet locked status
    const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxWalletLocked);
    console.log('Max wallet locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting max wallet locked:', error);
    return false;
  }
};

const detectMaxBuyLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);
    
    // Try to get the max buy locked status
    const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxBuyLocked);
    console.log('Max buy locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting max buy locked:', error);
    return false;
  }
};

const detectMaxSellLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);
    
    // Try to get the max sell locked status
    const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.maxSellLocked);
    console.log('Max sell locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting max sell locked:', error);
    return false;
  }
};

const detectTradingDelayLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);
    
    // Try to get the trading delay locked status
    const isLocked = await tryMultipleFunctions(contract, FUNCTION_VARIATIONS.tradingDelayLocked);
    console.log('Trading delay locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting trading delay locked:', error);
    return false;
  }
};

const fetchTaxDistributionFeatures = async (contract: any) => {
  if (!contract) return [];

  const features = [
    {
      name: 'Reflections',
      description: 'Token reflection rewards',
      icon: RefreshCw,
      getFee: ['reflectionFee', 'rewardsFee', 'reflectFee'],
      getLocked: ['reflectionLocked', 'rewardsLocked', 'reflectLocked'],
      lockFunction: ['lockReflection', 'lockRewards', 'lockReflect']
    },
    {
      name: 'Auto-Liquidity',
      description: 'Automatic liquidity generation',
      icon: Droplets,
      getFee: ['liquidityFee', 'autoLiquidityFee'],
      getLocked: ['autoLiquidityLocked'],
      lockFunction: ['lockAutoLiquidity']
    },
    {
      name: 'Burn',
      description: 'Token burning mechanism',
      icon: Flame,
      getFee: ['burnFee'],
      getLocked: ['burnLocked'],
      lockFunction: ['lockBurn']
    },
    {
      name: 'Marketing',
      description: 'Marketing wallet funding',
      icon: TrendingUp,
      getFee: ['marketingFee'],
      getLocked: ['marketingLocked'],
      lockFunction: ['lockMarketing', 'lockMarketingFee'] // Updated to use tax distribution lock functions
    },
    {
      name: 'Development',
      description: 'Development wallet funding',
      icon: Code2,
      getFee: ['developmentFee'],
      getLocked: ['developmentLocked'],
      lockFunction: ['lockDevelopment', 'lockDevelopmentFee'] // Updated to use tax distribution lock functions
    }
  ];

  try {
    const featuresWithStatus = await Promise.all(
      features.map(async (feature) => {
        let fee = null;
        let locked = false;
        let available = false;

        try {
          // Check if getLocked function exists and call it
          const lockedFuncName = findAvailableFunction(contract, feature.getLocked);
          if (lockedFuncName) {
            locked = await contract[lockedFuncName]();
            available = true;
          }

          // Try to get fee if getFee function exists
          const feeFuncName = findAvailableFunction(contract, feature.getFee);
          if (feeFuncName) {
            fee = await contract[feeFuncName]();
          }
        } catch (error) {
          console.error(`Error checking ${feature.name}:`, error);
        }

        return {
          ...feature,
          fee,
          locked,
          available
        };
      })
    );

    return featuresWithStatus.filter(f => f.available);
  } catch (error) {
    console.error('Error fetching tax distribution features:', error);
    throw new Error('Failed to fetch tax distribution features');
  }
};

const handleLockFeature = async (feature: any) => {
  if (!contract) return;
  const { settings: fees } = useFirebaseFees();
  const { chain } = useNetwork();
  const { data: walletClient } = useWalletClient();

  if (!fees?.networkFees) {
    toast({
      title: "Error",
      description: "Fee settings not available",
      variant: "destructive"
    });
    return;
  }

  try {
    setIsLoading(true);
    setTransactionStep(1);

    // Get signer and connect contract
    const provider = new ethers.providers.Web3Provider(walletClient.transport);
    const signer = provider.getSigner();
    const contractWithSigner = contract.connect(signer);

    // First transaction: Pay platform fee using settings
    const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
    const feeCollectorAddress = fees.collectors[0].address;
    
    try {
      await sendPlatformFee(signer, fees, chain);
      
      toast({
        title: "Platform Fee Paid",
        description: "Successfully paid the platform fee. Please confirm the next transaction to lock the feature.",
      });
    } catch (error) {
      console.error("Error paying platform fee:", error);
      toast({
        title: "Error",
        description: "Failed to pay platform fee. The feature cannot be locked.",
        variant: "destructive",
      });
      setIsLoading(false);
      setTransactionStep(0);
      return;
    }

    // Second transaction: Lock the feature
    setTransactionStep(2);
    
    // Find the correct lock function name
    const lockFunctionVariations = {
      reflection: ['lockReflectionFee', 'lockReflection', 'lockReflect', 'lockReflectionDistribution'],
      autoLiquidity: ['lockAutoLiquidityFee', 'lockAutoLiquidity', 'lockLiquidity', 'lockAutoLiquidityDistribution'],
      burn: ['lockBurnFee', 'lockBurn', 'lockBurnDistribution'],
      marketing: ['lockMarketingFee', 'lockMarketing', 'lockMarketingDistribution'],
      development: ['lockDevelopmentFee', 'lockDevelopment', 'lockDevelopmentDistribution']
    };

    const variations = lockFunctionVariations[feature];
    if (!variations) {
      throw new Error(`Invalid feature: ${feature}`);
    }

    // Try to find an available function
    let lockFunction = null;
    for (const funcName of variations) {
      if (typeof contractWithSigner[funcName] === 'function') {
        lockFunction = funcName;
        break;
      }
    }

    if (!lockFunction) {
      throw new Error(`No available lock function found for ${feature}`);
    }

    toast({
      title: "Locking Feature",
      description: "Please confirm the transaction to lock the feature...",
    });

    const tx = await contractWithSigner[lockFunction]({
      gasLimit: 300000
    });
    await tx.wait();

    // Update the distribution and lock status
    await refreshDistribution();

    toast({
      title: "Success",
      description: `${feature} has been locked successfully`,
    });
  } catch (error) {
    console.error(`Error locking ${feature}:`, error);
    toast({
      title: "Error",
      description: error.message || "Failed to lock feature",
      variant: "destructive",
    });
  } finally {
    setIsLoading(false);
    setTransactionStep(0);
  }
};

const getCurrentWallets = async (contract: any, type: 'marketing' | 'development') => {
  try {
    const wallets: string[] = [];
    let index = 0;

    while (true) {
      try {
        const wallet = await contract[`${type}Wallets`](index);
        if (!wallet || wallet === '0x0000000000000000000000000000000000000000') {
          break;
        }
        wallets.push(wallet);
        index++;
      } catch (error) {
        break;
      }
    }

    console.log(`${type} wallets:`, wallets);
    return wallets;
  } catch (error) {
    console.error(`Error getting ${type} wallets:`, error);
    return [];
  }
};

const WalletLocks = ({ contract }) => {
  const [features, setFeatures] = useState({
    marketingWallet: { value: null, isLocked: false, address: '' },
    developmentWallet: { value: null, isLocked: false, address: '' }
  });

  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  const [isOwner, setIsOwner] = useState(false);
  const provider = new ethers.providers.Web3Provider(walletClient.transport);
  const { chain } = useNetwork();

  const fetchWalletFeatures = async () => {
    if (!contract) return;

    try {
      // Get marketing wallet lock status
      const marketingLocked = await detectMarketingWalletLocked(contract);
      const marketingWallets = await getCurrentWallets(contract, 'marketing');
      console.log('Marketing wallet locked status:', marketingLocked);
      console.log('Marketing wallets:', marketingWallets);

      // Get development wallet lock status
      const developmentLocked = await detectDevelopmentWalletLocked(contract);
      const developmentWallets = await getCurrentWallets(contract, 'development');
      console.log('Development wallet locked status:', developmentLocked);
      console.log('Development wallets:', developmentWallets);

      setFeatures({
        marketingWallet: {
          value: 'Configured',
          isLocked: marketingLocked,
          address: marketingWallets[0] || ''
        },
        developmentWallet: {
          value: 'Configured',
          isLocked: developmentLocked,
          address: developmentWallets[0] || ''
        }
      });
    } catch (error) {
      console.error('Error fetching wallet features:', error);
    }
  };

  const handleLock = async (feature) => {
    if (!contract || !isOwner || !fees) {
      toast({
        title: "Error",
        description: "Unable to lock wallet. Please check your connection and permissions.",
        variant: "destructive"
      });
      return;
    }

    try {
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Platform fee transaction using Firebase fees
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
      const feeCollectorAddress = fees.collectors[0].address;

      // Send platform fee
      const feeTx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(chain?.id, 50000)
      });
      await feeTx.wait();

      // Get the lock function name based on the feature
      const lockFunctionMap = {
        marketingWallet: 'lockMarketingWallets',
        developmentWallet: 'lockDevelopmentWallets'
      };

      const lockFunction = lockFunctionMap[feature];
      if (!lockFunction) {
        throw new Error(`Invalid feature: ${feature}`);
      }

      // Lock the wallet
      const tx = await contractWithSigner[lockFunction]();
      await tx.wait();

      // Update the UI
      await fetchWalletFeatures();

      toast({
        title: "Success",
        description: `${feature === 'marketingWallet' ? 'Marketing' : 'Development'} wallet has been locked successfully`,
      });
    } catch (error) {
      console.error('Error locking wallet:', error);
      toast({
        title: "Error",
        description: `Failed to lock ${feature === 'marketingWallet' ? 'marketing' : 'development'} wallet. Please ensure the contract supports this feature.`,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const init = async () => {
      if (contract) {
        try {
          // Check if user is owner
          const owner = await contract.owner();
          setIsOwner(owner.toLowerCase() === userAddress?.toLowerCase());

          // Fetch wallet features
          await fetchWalletFeatures();
        } catch (error) {
          console.error('Error initializing:', error);
        }
      }
    };

    init();
  }, [contract, userAddress]);

  return (
    <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
      <div className="space-y-2 text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Wallet Locks</h2>
        <p className="text-sm text-muted-foreground">Lock marketing and development wallet settings</p>
      </div>

      <div className="space-y-4">
        {Object.entries(features).map(([key, feature]) => (
          <div key={key} className="flex items-center justify-between p-4 rounded-lg bg-card border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/10">
                <Wallet className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="font-medium">
                  {key === 'marketingWallet' ? 'Marketing Wallet' : 'Development Wallet'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.isLocked ? 'Locked' : 'Unlocked'}
                </p>
                {feature.address && (
                  <p className="text-xs text-blue-400 mt-1 break-all">
                    {feature.address}
                  </p>
                )}
              </div>
            </div>

            {feature.isLocked ? (
              <div className="bg-card px-3 py-1.5 rounded-lg border border-[#0a74ff]/50">
                <div className="flex items-center text-green-500 text-sm gap-1">
                  <Lock className="w-4 h-4" />
                  Locked
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLock(key)}
                disabled={feature.isLocked || !isOwner}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#0a74ff]/50 hover:bg-[#0a74ff]/10"
              >
                <Lock className="w-4 h-4" />
                Lock
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const detectMarketingWalletLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);

    // Try to get the marketing wallets array
    try {
      const marketingWalletsLength = await contract.marketingWallets.length;
      console.log('Marketing wallets length:', marketingWalletsLength);

      // Try to get the first marketing wallet
      if (marketingWalletsLength > 0) {
        const firstWallet = await contract.marketingWallets(0);
        console.log('First marketing wallet:', firstWallet);
      }
    } catch (error) {
      console.error('Error reading marketing wallets array:', error);
    }

    // Get the lock status
    const isLocked = await tryMultipleFunctions(contract, [
      'marketingWalletsLocked',
      'marketingWalletLocked',
      'isMarketingWalletLocked',
      'marketingLocked',
      'isMarketingLocked'
    ]);
    console.log('Marketing wallets locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting marketing wallets locked:', error);
    return false;
  }
};

const detectDevelopmentWalletLocked = async (contract: any) => {
  try {
    console.log('Contract address:', contract.address);

    // Try to get the development wallets array
    try {
      const developmentWalletsLength = await contract.developmentWallets.length;
      console.log('Development wallets length:', developmentWalletsLength);

      // Try to get the first development wallet
      if (developmentWalletsLength > 0) {
        const firstWallet = await contract.developmentWallets(0);
        console.log('First development wallet:', firstWallet);
      }
    } catch (error) {
      console.error('Error reading development wallets array:', error);
    }

    // Get the lock status
    const isLocked = await tryMultipleFunctions(contract, [
      'developmentWalletsLocked',
      'developmentWalletLocked',
      'isDevelopmentWalletLocked',
      'developmentLocked',
      'isDevelopmentLocked'
    ]);
    console.log('Development wallets locked status:', isLocked);
    return isLocked;
  } catch (error) {
    console.error('Error detecting development wallets locked:', error);
    return false;
  }
};

const tryMultipleFunctions = async (contract: any, functionNames: string[]) => {
  for (const funcName of functionNames) {
    if (contract[funcName]) {
      try {
        const result = await contract[funcName]();
        console.log(`Successfully called ${funcName}:`, result);
        if (typeof result === 'boolean') {
          return result;
        }
        // If result is a BigNumber, convert it to boolean
        if (result?._isBigNumber) {
          return !result.isZero();
        }
        // If result is a number, convert it to boolean
        if (typeof result === 'number') {
          return result !== 0;
        }
      } catch (error) {
        console.error(`Error calling ${funcName}:`, error);
      }
    } else {
      console.log(`Function ${funcName} not found on contract`);
    }
  }
  return false;
};

const MasterLock = ({ contract }) => {
  const { settings: fees } = useFirebaseFees();
  const { chain } = useNetwork();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [transactionStep, setTransactionStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const checkLockStatus = async () => {
    if (contract) {
      try {
        const locked = await contract.contractLocked();
        setIsLocked(locked);
      } catch (error) {
        console.error('Error checking lock status:', error);
      }
    }
  };

  useEffect(() => {
    checkLockStatus();
  }, [contract]);

  const handleLock = async () => {
    if (!contract || !confirmed) return;

    try {
      setIsLoading(true);
      setTransactionStep(1);

      // Get signer and connect contract
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee
      try {
        await sendPlatformFee(signer, fees, chain, 'masterLockFee');
        
        toast({
          title: "Platform Fee Paid",
          description: "Successfully paid the platform fee. Please confirm the next transaction to lock the contract.",
        });
      } catch (error) {
        console.error("Error paying platform fee:", error);
        toast({
          title: "Error",
          description: "Failed to pay platform fee. Contract cannot be locked.",
          variant: "destructive",
        });
        setIsLoading(false);
        setTransactionStep(0);
        return;
      }

      // Second transaction: Lock contract
      setTransactionStep(2);
      
      const tx = await contractWithSigner.lockContract({
        ...getTxOptions(chain?.id, 300000)
      });
      await tx.wait();

      toast({
        title: "Success",
        description: "Contract has been locked successfully",
      });

      // Update lock status
      await checkLockStatus();
    } catch (error) {
      console.error("Error locking contract:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to lock contract",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setTransactionStep(0);
      setConfirmed(false);
    }
  };

  if (isLocked) {
    return (
      <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Master Lock</h2>
          <div className="flex items-center justify-center gap-2 text-green-500 mt-4">
            <Lock className="w-5 h-5" />
            <span className="text-lg font-medium">Contract is Master Locked</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 rounded-lg border border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors bg-card">
      <div className="space-y-2 text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Master Lock</h2>
        <p className="text-sm text-muted-foreground">Master lock the contract to prevent any further changes</p>
      </div>

      <div className="p-4 mb-4 rounded-lg bg-destructive/5 border border-destructive/50 hover:border-destructive/70 transition-colors mt-4">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="w-5 h-5" />
          <span>Warning</span>
        </div>
        <div className="text-sm text-destructive/90">
          Activating the Master Lock will permanently lock all contract functions. This action cannot be undone.
          Please ensure all other necessary locks are in place before proceeding.
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox 
            id="confirm"
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
          />
          <label 
            htmlFor="confirm" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I understand that this action is irreversible and will permanently lock the contract
          </label>
        </div>

        <Button
          className="w-full"
          variant="destructive"
          disabled={!confirmed}
          onClick={handleLock}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {transactionStep === 1 ? "Processing Fee..." : "Locking..."}
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 mr-2" />
              Activate Master Lock
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const RenounceOwnership = ({ contract }) => {
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { settings: fees } = useFirebaseFees();
  const { chain } = useNetwork();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [transactionStep, setTransactionStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  const handleRenounceOwnership = async () => {
    if (!contract || !address || !confirmed) {
      return;
    }

    try {
      setIsLoading(true);
      setTransactionStep(1);

      // Get signer and connect contract
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee
      try {
        await sendPlatformFee(signer, fees, chain, 'masterLockFee');
        
        toast({
          title: "Platform Fee Paid",
          description: "Successfully paid the platform fee. Please confirm the next transaction to renounce ownership.",
        });
      } catch (error) {
        console.error("Error paying platform fee:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to pay platform fee. Ownership cannot be renounced.",
          variant: "destructive",
        });
        setIsLoading(false);
        setTransactionStep(0);
        return;
      }

      // Second transaction: Renounce ownership
      setTransactionStep(2);
      const tx = await contractWithSigner.renounceOwnership({
        ...getTxOptions(chain?.id, 300000)
      });
      await tx.wait();

      toast({
        title: "Success",
        description: "Successfully renounced ownership",
      });
    } catch (error) {
      console.error("Error renouncing ownership:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to renounce ownership",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setTransactionStep(0);
      setConfirmed(false);
    }
  };

  return (
    <Card className="w-full border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-red-500" />
          <CardTitle>Renounce Ownership</CardTitle>
        </div>
        <CardDescription>
          Permanently renounce contract ownership. This action cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Critical Warning</AlertTitle>
          <AlertDescription>
            Renouncing ownership means you will permanently lose all owner privileges:
            <ul className="list-disc list-inside mt-2">
              <li>You will not be able to modify any contract settings</li>
              <li>You will not be able to update any parameters</li>
              <li>You will not be able to perform any administrative functions</li>
              <li>No one will have owner access to the contract</li>
            </ul>
            This action is absolutely irreversible! Make sure you want to proceed.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="renounce-confirm"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
            />
            <label
              htmlFor="renounce-confirm"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I understand that renouncing ownership is permanent and cannot be reversed under any circumstances
            </label>
          </div>

          <Button
            variant="destructive"
            className="w-full"
            disabled={!confirmed}
            onClick={handleRenounceOwnership}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {transactionStep === 1 ? "Processing Fee..." : "Renouncing Ownership..."}
              </>
            ) : (
              "Renounce Contract Ownership"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const TradingEnabledLock = ({ contract }) => {
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [tradingEnabled, setTradingEnabled] = useState(false);
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const [transactionStep, setTransactionStep] = useState(0);
  const { chain } = useNetwork();

  useEffect(() => {
    const checkStatus = async () => {
      if (!contract || !userAddress) return;

      try {
        // Check if user is owner
        const owner = await contract.owner();
        setIsOwner(owner.toLowerCase() === userAddress.toLowerCase());

        // Check if trading is locked
        const locked = await contract.tradingEnabledLocked();
        setIsLocked(locked);

        // Check current trading status
        const enabled = await contract.tradingEnabled();
        setTradingEnabled(enabled);
      } catch (err) {
        console.error("Error checking status:", err);
        setError("Failed to check contract status");
      }
    };

    checkStatus();
  }, [contract, userAddress]);

  const handleLock = async () => {
    if (!contract || !isOwner) {
      setError("You must be the contract owner to lock trading status");
      return;
    }

    if (!fees) {
      toast({
        title: "Error",
        description: "Fee settings not available",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      setTransactionStep(1);

      // Get signer and connect contract
      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // First transaction: Pay platform fee using settings
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee(fees, chain));
      const feeCollectorAddress = fees.collectors[0].address;

      try {
        const feeTx = await signer.sendTransaction({
          to: feeCollectorAddress,
          value: platformFeeAmount
        });

        toast({
          title: "Processing Fee Payment",
          description: "Please wait while the fee payment is being processed...",
        });
        
        await feeTx.wait();
        
        toast({
          title: "Platform Fee Paid",
          description: "Successfully paid the platform fee. Please confirm the next transaction to lock trading status.",
        });
      } catch (error) {
        console.error("Error paying platform fee:", error);
        toast({
          title: "Error",
          description: "Failed to pay platform fee. Trading status cannot be locked.",
          variant: "destructive",
        });
        setIsLoading(false);
        setTransactionStep(0);
        return;
      }

      // Second transaction: Lock trading status
      setTransactionStep(2);
      
      toast({
        title: "Locking Trading Status",
        description: "Please confirm the transaction to lock trading status...",
      });

      const tx = await contractWithSigner.lockTradingEnabled();
      await tx.wait();
      
      setIsLocked(true);
      toast({
        title: "Success",
        description: "Trading status has been locked successfully",
      });
    } catch (err) {
      console.error("Error locking trading enabled:", err);
      setError(err.message || "Failed to lock trading enabled");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to lock trading status. Make sure you are the contract owner.",
      });
    } finally {
      setIsLoading(false);
      setTransactionStep(0);
    }
  };

  return (
    <Card className="border-[#0a74ff]/50 hover:border-[#0a74ff]/70 transition-colors">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Trading Status Lock</CardTitle>
            <CardDescription>
              Lock the trading enabled/disabled status permanently
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {isLocked ? "Locked" : "Unlocked"}
              </div>
            </div>
            <div className={cn(
              "px-3 py-1 rounded-full text-sm",
              isLocked 
                ? "bg-green-500/10 text-green-500" 
                : isOwner 
                  ? "bg-yellow-500/10 text-yellow-500"
                  : "bg-red-500/10 text-red-500"
            )}>
              {isLocked 
                ? "Locked" 
                : isOwner 
                  ? "Ready to Lock" 
                  : "Owner Required"}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 flex flex-col items-center">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1 text-center">
              <div className="text-sm font-medium text-muted-foreground">Lock Status</div>
              <div className="font-medium">{isLocked ? "Locked" : "Unlocked"}</div>
            </div>
            <div className="space-y-1 text-center">
              <div className="text-sm font-medium text-muted-foreground">Trading Status</div>
              <div className="font-medium flex items-center justify-center gap-2">
                {tradingEnabled ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-green-500">Enabled</span>
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4 text-red-500" />
                    <span className="text-red-500">Disabled</span>
                  </>
                )}
              </div>
            </div>
            {transactionStep > 0 && (
              <div className="space-y-1 text-center col-span-2">
                <div className="text-sm font-medium text-muted-foreground">Transaction Progress</div>
                <div className="font-medium">
                  {transactionStep === 1 ? "Processing Fee Payment" : "Locking Trading Status"}
                </div>
              </div>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleLock}
            disabled={isLocked || isLoading || !isOwner}
            className="w-fit"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {transactionStep === 1 ? "Processing Fee..." : "Locking..."}
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Lock Trading Status
              </>
            )}
          </Button>

          {error && (
            <Alert variant="destructive" className="w-full text-center">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Alert className="w-full text-center">
            <AlertTriangle className="w-4 h-4 mx-auto mb-2" />
            <AlertTitle className="text-center">Warning</AlertTitle>
            <AlertDescription>
              Once locked, the trading enabled/disabled status cannot be changed anymore. This action cannot be undone.
            </AlertDescription>
          </Alert>
        </div>
      </CardContent>
    </Card>
  );
};

const SmartContractLocker = () => {
  const { address: userAddress, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { toast } = useToast();
  const { chain } = useNetwork();
  const [contractAddress, setContractAddress] = useState("");
  const [contract, setContract] = useState(null);
  const [tokenInfo, setTokenInfo] = useState({ name: "", symbol: "" });
  const [ownerAddress, setOwnerAddress] = useState("");
  const [totalSupply, setTotalSupply] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [debugLoaded, setDebugLoaded] = useState(false);
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [showWarningFlash, setShowWarningFlash] = useState(false);

  // Get network-specific names
  const getNetworkNames = () => {
    if (!chain) return { networkName: "BSC", networkDesc: "BSC" };
    
    switch (chain.id) {
      case 1: // Ethereum Mainnet
        return { networkName: "Ethereum", networkDesc: "Ethereum Mainnet" };
      case 11155111: // Sepolia
        return { networkName: "Sepolia", networkDesc: "Sepolia Testnet" };
      case 56: // BSC
        return { networkName: "BSC", networkDesc: "BSC" };
      case 97: // BSC Testnet
        return { networkName: "BSC Testnet", networkDesc: "BSC Testnet" };
      case 43114: // Avalanche
        return { networkName: "Avalanche", networkDesc: "Avalanche" };
      case 43113: // Fuji (Avalanche Testnet)
        return { networkName: "Fuji", networkDesc: "Avalanche Fuji Testnet" };
      case 2741: // Abstract Mainnet
        return { networkName: "Abstract", networkDesc: "Abstract Mainnet" };
      case 11124: // Abstract Testnet
        return { networkName: "Abstract Sepolia", networkDesc: "Abstract Sepolia Testnet" };
      case 8453: // Base Mainnet
        return { networkName: "Base", networkDesc: "Base" };
      case 84532: // Base Sepolia
        return { networkName: "Base Sepolia", networkDesc: "Base Sepolia Testnet" };
      case 10: // OP Mainnet
        return { networkName: "OP", networkDesc: "OP Mainnet" };
      case 11155420: // OP Sepolia
        return { networkName: "OP Sepolia", networkDesc: "OP Sepolia Testnet" };
      case 42161: // Arbitrum One
        return { networkName: "Arbitrum", networkDesc: "Arbitrum One" };
      case 421614: // Arbitrum Sepolia
        return { networkName: "Arbitrum Sepolia", networkDesc: "Arbitrum Sepolia Testnet" };
      case 137: // Polygon Mainnet
        return { networkName: "Polygon", networkDesc: "Polygon" };
      case 80002: // Polygon Amoy Testnet
        return { networkName: "Polygon Amoy", networkDesc: "Polygon Amoy Testnet" };
      case 1328: // Sei Atlantic-2 Testnet
        return { networkName: "Sei", networkDesc: "Sei Atlantic-2 Testnet" };
      case 1329: // Sei Pacific-1 Mainnet
        return { networkName: "Sei", networkDesc: "Sei Pacific-1 Mainnet" };
      default:
        return { networkName: "BSC", networkDesc: "BSC" };
    }
  };

  const { networkName, networkDesc } = getNetworkNames();
  const validateContract = async () => {
    if (!isConnected) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet first.",
        variant: "destructive"
      });
      return;
    }

    if (!contractAddress || !ethers.utils.isAddress(contractAddress)) {
      toast({
        title: "Invalid Contract",
        description: "Please enter a valid contract address.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setTokenInfo({ name: "", symbol: "" });
    setOwnerAddress("");
    
    try {
      if (!walletClient) {
        throw new Error("No wallet client found. Please make sure your wallet is connected.");
      }

      const provider = new ethers.providers.Web3Provider(walletClient.transport);
      const contractInstance = new ethers.Contract(contractAddress, [...ERC20_ABI, ...TAX_ABI], provider);
      setContract(contractInstance);

      // Get basic token info
      const [name, symbol, owner] = await Promise.all([
        contractInstance.name(),
        contractInstance.symbol(),
        contractInstance.owner()
      ]);

      setTokenInfo({ name, symbol });
      setOwnerAddress(owner);

      // Check if connected wallet is owner
      const isOwner = owner.toLowerCase() === userAddress?.toLowerCase();
      setIsOwner(isOwner);

      toast({
        title: "Success",
        description: "Contract verified successfully",
      });
    } catch (error) {
      console.error("Error verifying contract:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to verify contract. Please check the address and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalSupply = async () => {
    try {
      const supply = await contract.totalSupply();
      const formattedSupply = ethers.utils.formatUnits(supply, await contract.decimals());
      setTotalSupply(formattedSupply);
    } catch (error) {
      console.error("Error fetching total supply:", error);
    }
  };

  useEffect(() => {
    if (contract) {
      fetchTotalSupply();
    }
  }, [contract]);

  return (
    <div className="min-h-[calc(100vh-theme(spacing.32))] flex flex-col relative">
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col gap-4 relative z-10">
        <div className="container mx-auto p-6 space-y-6">
          <div className="space-y-2 text-center relative mt-8">
            <div className="flex items-center justify-center gap-4">
              <Unlock className="w-8 h-8" style={{
                color: `var(--color-header-accent, #0A74FF)`
              }} />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent pb-2 leading-tight whitespace-nowrap" style={{
                  backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>{networkName} Smart Contract Locker</h1>
                <p className="text-muted-foreground">
                  Lock your smart contract features to enhance security and trust
                </p>
              </div>
              <Lock className="w-8 h-8" style={{
                color: `var(--color-secondary, #10b981)`
              }} />
            </div>
          </div>

          <div className="grid gap-6">
            <div className="relative rounded-lg">
              <div className="absolute inset-0 rounded-lg" style={{
                background: `linear-gradient(to right, var(--color-header-accent, #0A74FF) 45%, var(--color-secondary, #10b981) 55%, var(--color-secondary, #10b981))`
              }} />
              <div className="absolute inset-[1px] rounded-lg dark:bg-black bg-white" />
              <Card className="relative bg-background/30 backdrop-blur-sm rounded-lg border-0">
                <CardHeader className="text-center">
                  <div className="flex flex-col items-center gap-4 justify-center">
                    <CardTitle className="text-2xl">MdbApp Contract Locks Scanner</CardTitle>
                    <CardDescription className="text-gray-400">
                      Lock your token contract features to enhance security and build trust with your community.
                      <br />
                      Scan/Analyze/Lock your {networkDesc} token contracts features. Tool is designed for Contracts deployed by the MdbApp.io factory.
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
                    <UserTokenSelector
                      onSelectToken={(address) => setContractAddress(address)}
                      selectedToken={contractAddress}
                      label="Quick Select Your Token"
                    />
                    <div className="relative rounded-md w-full">
                      <div className="absolute inset-0 rounded-md" style={{
                        background: `linear-gradient(to right, var(--color-header-accent, #0A74FF) 45%, var(--color-secondary, #10b981) 55%, var(--color-secondary, #10b981))`
                      }} />
                      <div className="absolute inset-[1px] rounded-[calc(0.375rem-1px)] dark:bg-black bg-white" />
                      <Input
                        placeholder="Enter contract address or select from your tokens above"
                        value={contractAddress}
                        onChange={(e) => setContractAddress(e.target.value)}
                        className="relative w-full bg-black/50 border-0 dark:bg-black/30 bg-white/80 backdrop-blur-sm text-foreground placeholder:text-muted-foreground dark:placeholder:text-gray-400 text-center"
                      />
                    </div>
                    <div className="relative rounded-md w-full">
                      <div className="absolute inset-0 rounded-md" style={{
                        background: `linear-gradient(to right, var(--color-header-accent, #0A74FF) 45%, var(--color-secondary, #10b981) 55%, var(--color-secondary, #10b981))`
                      }} />
                      <div className="absolute inset-[1px] rounded-[calc(0.375rem-1px)] dark:bg-black bg-white" />
                      <Button
                        onClick={validateContract}
                        disabled={loading}
                        className="relative w-full dark:bg-black/30 bg-white/30 backdrop-blur-sm border-0 hover:bg-[#ef4444]/10 transition-colors text-foreground font-semibold"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading
                          </>
                        ) : (
                          "Load Contract"
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {contract && (
            <>
              <Card className="relative bg-background/30 backdrop-blur-sm">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl flex items-center justify-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-green-500" />
                    Token Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2 flex flex-col items-center">
                      <div className="flex items-center justify-center gap-2">
                        <Code className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Token Name:</span>
                        <span className="text-sm text-muted-foreground">{tokenInfo.name}</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <CircleDollarSign className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Symbol:</span>
                        <span className="text-sm text-muted-foreground">{tokenInfo.symbol}</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <BarChart2 className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Total Supply:</span>
                        <span className="text-sm text-muted-foreground">
                          {totalSupply ? Number(totalSupply).toLocaleString() : "Loading..."}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2 flex flex-col items-center">
                      <div className="flex items-center justify-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Connected as Owner:</span>
                        <Badge variant={isOwner ? "default" : "destructive"}>
                          {isOwner ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Check className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Contract Verified:</span>
                        <Badge variant="default">Yes</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center text-center border-t pt-4 mt-2">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">Owner Address:</span>
                    </div>
                    <div className="max-w-full px-4 text-center">
                      <span className="text-sm text-muted-foreground break-all">{ownerAddress}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {isOwner ? (
                <>
                  <TaxRateLocks contract={contract} />
                  <TaxDistributionLocks contract={contract} />
                  <WalletLocks contract={contract} />
                  <SecurityLocks contract={contract} />
                  <TradingEnabledLock contract={contract} />
                  <MasterLock contract={contract} />
                  <RenounceOwnership contract={contract} />
                </>
              ) : (
                <Alert variant="destructive" className="mt-4">
                  <div className="flex flex-col items-center text-center w-full">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Access Denied</AlertTitle>
                    </div>
                    <AlertDescription className="text-center">
                      You must be the contract owner to view and modify contract locks.
                      <div className="mt-2">
                        Current owner: {ownerAddress}
                      </div>
                    </AlertDescription>
                  </div>
                </Alert>
              )}
            </>
          )}
        </div>

        <div className="container flex flex-col items-center justify-center gap-4 px-4 mt-8">
          <h2 className="text-3xl font-bold mb-8 tracking-tight text-center text-transparent bg-clip-text" style={{
            backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
          }}>
            Frequently Asked Questions
          </h2>
          <div className="relative rounded-lg w-full">
            <div className="absolute inset-0 rounded-lg" style={{
              background: `linear-gradient(to right, var(--color-header-accent, #0A74FF) 45%, var(--color-secondary, #10b981) 55%, var(--color-secondary, #10b981))`
            }} />
            <div className="absolute inset-[1px] rounded-lg dark:bg-black bg-white" />
            <Card className="relative w-full bg-background/30 backdrop-blur-sm rounded-lg border-0">
              {faqData.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </Card>
          </div>
        </div>

        {selectedFeature && (
          <TaxDistributionDialog
            contract={contract}
            feature={selectedFeature}
            onClose={() => setSelectedFeature(null)}
          />
        )}
      </div>
    </div>
  );
};

export { SmartContractLocker };

const getTaxDistribution = async (contract) => {
  try {
    const distribution = await contract.taxDistribution();
    
    // Get lock statuses
    const reflectionLocked = await tryMultipleFunctions(contract, ['reflectionLocked', 'rewardsLocked', 'reflectLocked']);
    const autoLiquidityLocked = await tryMultipleFunctions(contract, ['autoLiquidityLocked', 'liquidityLocked', 'autoLiquidityFeeLocked']);
    const burnLocked = await tryMultipleFunctions(contract, ['burnLocked', 'burnFeeLocked']);
    const marketingLocked = await tryMultipleFunctions(contract, ['marketingLocked', 'marketingFeeLocked']);
    const developmentLocked = await tryMultipleFunctions(contract, ['developmentLocked', 'developmentFeeLocked']);

    return {
      reflection: {
        value: distribution.reflection?.toString() || "0",
        locked: reflectionLocked
      },
      autoLiquidity: {
        value: distribution.autoLiquidity?.toString() || "0",
        locked: autoLiquidityLocked
      },
      burn: {
        value: distribution.burn?.toString() || "0",
        locked: burnLocked
      },
      marketing: {
        value: distribution.marketing?.toString() || "0",
        locked: marketingLocked
      },
      development: {
        value: distribution.development?.toString() || "0",
        locked: developmentLocked
      }
    };
  } catch (e) {
    console.error("Failed to get tax distribution:", e);
    return {
      reflection: { value: "0", locked: false },
      autoLiquidity: { value: "0", locked: false },
      burn: { value: "0", locked: false },
      marketing: { value: "0", locked: false },
      development: { value: "0", locked: false }
    };
  }
};

const findAvailableFunction = (contract: any, functionNames: string[]) => {
  for (const funcName of functionNames) {
    if (contract[funcName]) {
      return funcName;
    }
  }
  return null;
};

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b last:border-b-0" style={{
      borderColor: `var(--color-header-accent, #0A74FF)30`
    }}>
      <button
        className="w-full py-5 px-4 flex justify-between items-center hover:opacity-80 transition-colors duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-lg font-medium" style={{
          color: `var(--color-header-accent, #0A74FF)`
        }}>{question}</span>
        <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{
          color: `var(--color-secondary, #10b981)`
        }}>
          ▼
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
        }`}
      >
        <p className="text-gray-300">{answer}</p>
      </div>
    </div>
  );
};

const faqData = [
  {
    question: "What is the Smart Contract Locker?",
    answer: "The Smart Contract Locker is a security tool that allows you to lock specific features of your token contract, such as ownership transfer, tax rate changes, and other administrative functions. This adds an extra layer of trust and security for your token holders."
  },
  {
    question: "What features can I lock?",
    answer: "You can lock various contract features including: ownership transfer, tax rate modifications, trading limits (max wallet, max buy/sell), and other administrative functions. You can also perform a Master Lock which locks all contract features at once."
  },
  {
    question: "How long can I lock features for?",
    answer: "You can set custom locks for each feature. Once locked, the feature cannot be modified. For maximum security, you can permanently lock features, Master Lock the contract, or renounce ownership entirely."
  },
  {
    question: "What is Master Lock?",
    answer: "Master Lock is a comprehensive security feature that locks all modifiable aspects of your contract at once. This provides maximum security and trust, as no changes can be made to any contract features while Master Lock is active."
  },
  {
    question: "Can I unlock features before the lock period ends?",
    answer: "No, once a feature is locked, it cannot be unlocked until the specified lock period expires. This is a security measure to ensure trust between token creators and holders. Plan your lock duration carefully."
  },
  {
    question: "What happens when I renounce ownership?",
    answer: "Renouncing ownership is permanent and irreversible. Once ownership is renounced, no one can modify any contract features - not even the original owner. This provides maximum decentralization but removes all ability to make future changes."
  }
];

const SmartContractLockerPage = () => {
  return (
    <div className="container flex flex-col items-center justify-center gap-4 px-4 mt-8">
      <h2 className="text-3xl font-bold mb-8 tracking-tight text-center text-transparent bg-clip-text" style={{
        backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
      }}>
        Frequently Asked Questions
      </h2>
      <div className="relative rounded-lg w-full">
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#ef4444] from-45% via-[#00ff00] via-55% to-[#00ff00]" />
        <div className="absolute inset-[1px] rounded-lg dark:bg-black bg-white" />
        <Card className="relative w-full bg-background/30 backdrop-blur-sm rounded-lg border-0">
          {faqData.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </Card>
      </div>
    </div>
  );
};

const SmartContractLockerApp = () => {
  return (
    <div>
      <SmartContractLocker />
      <SmartContractLockerPage />
    </div>
  );
};

export default SmartContractLockerApp;

const getNetworkFee = (fees: any, chain: any, feeType: 'lockFee' | 'masterLockFee' = 'lockFee') => {
  if (!fees?.networkFees || !chain?.id) return null;

  // Map testnet and L2 chain IDs to their mainnet counterparts for fee lookup
  const mainnetChainId = {
    // Ethereum family (use ETH fees)
    1: 1,        // Ethereum Mainnet
    5: 1,        // Goerli
    11155111: 1, // Sepolia
    11124: 1,    // Abstract Testnet
    2741: 1,     // Abstract Mainnet
    8453: 1,     // Base Mainnet
    84532: 1,    // Base Sepolia
    10: 1,       // OP Mainnet
    11155420: 1, // OP Sepolia
    42161: 1,    // Arbitrum One
    421614: 1,   // Arbitrum Sepolia

    // BSC
    56: 56,      // Mainnet
    97: 56,      // Testnet

    // Avalanche
    43114: 43114, // Mainnet
    43113: 43114  // Fuji Testnet
  }[chain.id] || chain.id;

  const networkFees = fees.networkFees[mainnetChainId];
  if (!networkFees) return null;

  return networkFees[feeType];
};

const sendPlatformFee = async (signer: any, fees: any, chain: any, feeType: 'lockFee' | 'masterLockFee' = 'lockFee') => {
  const fee = getNetworkFee(fees, chain, feeType);
  if (!fee) {
    throw new Error("Fee not configured for this network");
  }

  const feeCollector = fees.collectors[0].address;
  const platformFeeAmount = ethers.utils.parseEther(fee);

  console.log('Sending platform fee transaction...');
  console.log('Fee collector:', feeCollector);
  console.log('Fee amount:', platformFeeAmount.toString());

  const tx = await signer.sendTransaction({
    to: feeCollector,
    value: platformFeeAmount,
    ...getTxOptions(chain?.id, 50000)
  });

  await tx.wait();
  console.log('Platform fee transaction confirmed');
};
